import { NavLink } from "react-router-dom";
import {
  LayoutGrid, Building2, Inbox, Bell, FileText,
  FileEdit, Share2, Settings as SettingsIcon, ChevronsLeft, Upload,
  Users, Briefcase, ShieldCheck, CalendarDays,
} from "lucide-react";
import { BRAND } from "@avestate/shared";
import { cn } from "@/lib/cn.js";
import { useAuth } from "@/auth/useAuth.js";
import { roleLabel } from "@/lib/role.js";

type NavItem = { to: string; label: string; Icon: typeof LayoutGrid; group: "Lavoro" | "Setup" };
const ITEMS: NavItem[] = [
  { to: "/",             label: "Dashboard",    Icon: LayoutGrid,  group: "Lavoro" },
  { to: "/listings",     label: "Immobili",     Icon: Building2,   group: "Lavoro" },
  { to: "/import",       label: "Importa",      Icon: Upload,      group: "Lavoro" },
  { to: "/inbox",        label: "Inbox lead",   Icon: Inbox,       group: "Lavoro" },
  { to: "/agenda",       label: "Agenda visite", Icon: CalendarDays, group: "Lavoro" },
  { to: "/contacts",     label: "Contatti",     Icon: Users,       group: "Lavoro" },
  { to: "/deals",        label: "Pratiche",     Icon: Briefcase,   group: "Lavoro" },
  { to: "/adempimenti",  label: "Adempimenti",  Icon: ShieldCheck, group: "Lavoro" },
  { to: "/alerts",       label: "Alert acquirenti", Icon: Bell,    group: "Lavoro" },
  { to: "/blog",         label: "Blog",         Icon: FileText,    group: "Lavoro" },
  { to: "/pages",        label: "Pagine",       Icon: FileEdit,    group: "Lavoro" },
  { to: "/portals",      label: "Portali",      Icon: Share2,      group: "Setup" },
  { to: "/settings",     label: "Team & setup", Icon: SettingsIcon,group: "Setup" },
];

export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }): JSX.Element {
  const { email, role } = useAuth();
  const groups: Array<NavItem["group"]> = ["Lavoro", "Setup"];

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 flex h-screen flex-col border-r border-rule bg-surface transition-[width] duration-220 ease-out-quart",
        collapsed ? "w-[64px]" : "w-[240px]",
      )}
      aria-label="Navigazione principale"
    >
      <div className="flex h-[48px] items-center justify-between border-b border-rule px-4">
        {!collapsed && (
          <span className="font-display text-h2 tracking-tight">
            {BRAND.wordmark.left}<span className="text-accent">{BRAND.wordmark.slash}</span>{BRAND.wordmark.right}
          </span>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="ml-auto rounded p-1 text-ink-mute hover:bg-surface-2 hover:text-ink"
          aria-label={collapsed ? "Espandi sidebar" : "Comprimi sidebar"}
        >
          <ChevronsLeft className={cn("size-4 transition-transform duration-220", collapsed && "rotate-180")} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        {groups.map((g) => (
          <div key={g} className="mb-3">
            {!collapsed && (
              <p className="px-4 pb-1 font-mono text-meta uppercase tracking-wider text-ink-mute">{g}</p>
            )}
            {ITEMS.filter((i) => i.group === g).map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                className={({ isActive }) =>
                  cn(
                    "relative mx-2 flex items-center gap-3 rounded px-2 py-2 text-body text-ink-soft transition-colors duration-220 hover:bg-surface-2 hover:text-ink",
                    isActive &&
                      "bg-accent/10 text-ink [&_svg]:text-accent before:absolute before:-left-2 before:top-1/2 before:h-[18px] before:w-[3px] before:-translate-y-1/2 before:rounded-r before:bg-accent before:content-['']",
                    collapsed && "justify-center",
                  )
                }
              >
                <it.Icon className="size-4 shrink-0" />
                {!collapsed && <span>{it.label}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className={cn("border-t border-rule p-3", collapsed && "px-2")}>
        {!collapsed ? (
          <div className="space-y-1">
            <p className="truncate text-body-strong text-ink">{email ?? "—"}</p>
            <p className="font-mono text-meta uppercase text-ink-mute">{role ? roleLabel(role) : "—"}</p>
          </div>
        ) : (
          <div className="grid size-10 place-items-center rounded-pill bg-surface-2 font-mono text-meta uppercase text-ink">
            {(email ?? "?").slice(0, 2)}
          </div>
        )}
      </div>
    </aside>
  );
}
